import { formatCurrencyPrice } from '@/utils/currency';

interface ProviderProfileViewProps {
  currentDisplay: Api.Provider.ProviderProfileResponse | null;
  profile: Api.Provider.ProviderProfileResponse | null;
  user: any;
  getCategoryNameByCode: (code: string) => string;
  getCityNameById: (cityId?: string | number) => string;
  onPreviewWork: (work: Api.Provider.ProviderPortfolioItemResponse) => void;
  onStartEdit?: () => void;
}

export default function ProviderProfileView({
  currentDisplay,
  profile,
  user,
  getCategoryNameByCode,
  getCityNameById,
  onPreviewWork,
  onStartEdit,
}: ProviderProfileViewProps) {
  if (!profile && !currentDisplay) {
    return (
      <div className="py-16 px-4 text-center bg-gray-50/50 rounded-2xl border border-dashed border-gray-200/80">
        <div className="w-16 h-16 rounded-2xl bg-primary-50 text-primary-600 mx-auto flex items-center justify-center text-2xl mb-4 shadow-2xs">
          <i className="ri-user-add-line"></i>
        </div>
        <h3 className="text-base font-bold text-foreground-950 mb-1 font-heading">暂未填写档案</h3>
        <p className="text-xs text-foreground-500 max-w-md mx-auto mb-6 leading-relaxed">
          完善您的服务领域、兼全职状态、常驻城市及代表作品，即可在服务商大厅展示并获得更多合作机会。
        </p>
        {onStartEdit && (
          <button
            onClick={onStartEdit}
            className="px-6 py-2.5 rounded-xl bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 transition-all cursor-pointer shadow-sm inline-flex items-center gap-2"
          >
            <i className="ri-edit-box-line"></i>
            立即填写档案
          </button>
        )}
      </div>
    );
  }

  const works = currentDisplay?.portfolio || [];

  return (
    <div className="space-y-8">
      {/* 需要操作处理提示 */}
      {currentDisplay?.action_required && (
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200/80 text-amber-700 text-xs flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="ri-alert-line text-base shrink-0"></i>
            <span>当前服务档案需要您进一步补充或更正资料，请查看审核意见并修改后重新提交。</span>
          </div>
        </div>
      )}

      {/* 概览头部 */}
      <div className="p-6 rounded-2xl bg-gradient-to-r from-gray-50 to-primary-50/20 border border-gray-200/70 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-primary-100 text-primary-600 flex items-center justify-center font-bold text-xl overflow-hidden border border-primary-200 shrink-0 shadow-sm">
            {currentDisplay?.avatar?.access_url ? (
              <img src={currentDisplay.avatar.access_url} alt="头像" className="w-full h-full object-cover" />
            ) : (
              (currentDisplay?.display_name || user?.name || '创')[0]
            )}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-heading font-bold text-lg text-foreground-950">
                {currentDisplay?.display_name || user?.name || '未设置展示名称'}
              </h2>
              {currentDisplay?.provider_type === 'ENTERPRISE' && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium flex items-center gap-1">
                  <i className="ri-vip-crown-fill text-xs"></i> 企业团队
                </span>
              )}
              <span
                className={`text-[10px] px-2.5 py-0.5 rounded-full font-medium ${currentDisplay?.seeking_work !== false
                    ? 'bg-primary-500/15 text-primary-700'
                    : 'bg-foreground-400/15 text-foreground-600'
                  }`}
              >
                {currentDisplay?.seeking_work !== false ? '开放接单中' : '暂停接单'}
              </span>
            </div>

            {(currentDisplay?.enterprise_name || currentDisplay?.enterprise_name_masked) && (
              <p className="text-xs text-foreground-500 flex items-center gap-1 mt-1 font-medium">
                <i className="ri-building-line text-primary-500"></i>
                {currentDisplay.enterprise_name || currentDisplay.enterprise_name_masked}
              </p>
            )}

            {/* 擅长类目标签 */}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {currentDisplay?.category_codes && currentDisplay.category_codes.length > 0 ? (
                currentDisplay.category_codes.map((code) => (
                  <span
                    key={code}
                    className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-white text-primary-700 border border-primary-100 shadow-2xs"
                  >
                    {getCategoryNameByCode(code)}
                  </span>
                ))
              ) : (
                <span className="text-xs text-foreground-400">未设置业务类目</span>
              )}
            </div>
          </div>
        </div>

        <div className="text-left md:text-right border-t md:border-t-0 border-gray-200/60 pt-4 md:pt-0 w-full md:w-auto flex flex-row md:flex-col justify-between items-center md:items-end">
          <div>
            <p className="text-xs text-foreground-400">日薪标准</p>
            <p className="text-xl font-bold text-primary-600 font-heading">
              {formatCurrencyPrice(currentDisplay?.daily_rate, currentDisplay?.currency)}
              {currentDisplay?.daily_rate && <span className="text-xs font-normal text-foreground-500"> / 天</span>}
            </p>
          </div>
        </div>
      </div>

      {/* 核心字段网格展示 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl bg-gray-50/80 border border-gray-200/60">
          <p className="text-xs text-foreground-400 mb-1 flex items-center gap-1">
            <i className="ri-briefcase-line text-primary-500"></i> 服务方式
          </p>
          <p className="text-sm font-semibold text-foreground-950">
            {currentDisplay?.service_mode === 'FULL_TIME'
              ? '全职合作'
              : currentDisplay?.service_mode === 'PART_TIME'
                ? '兼职驻场'
                : currentDisplay?.service_mode === 'PROJECT'
                  ? '按项目交付'
                  : currentDisplay?.service_mode || '未设置'}
          </p>
        </div>
        <div className="p-4 rounded-xl bg-gray-50/80 border border-gray-200/60">
          <p className="text-xs text-foreground-400 mb-1 flex items-center gap-1">
            <i className="ri-map-pin-line text-primary-500"></i> 常驻城市
          </p>
          <p className="text-sm font-semibold text-foreground-950">
            {currentDisplay?.city_name || getCityNameById(currentDisplay?.city_code) || currentDisplay?.city_code || '全国 / 远程'}
          </p>
        </div>
        <div className="p-4 rounded-xl bg-gray-50/80 border border-gray-200/60">
          <p className="text-xs text-foreground-400 mb-1 flex items-center gap-1">
            <i className="ri-global-line text-primary-500"></i> 远程支持
          </p>
          <p className="text-sm font-semibold text-foreground-950">
            {currentDisplay?.remote ? '支持远程工作' : '现场履约'}
          </p>
        </div>
        <div className="p-4 rounded-xl bg-gray-50/80 border border-gray-200/60">
          <p className="text-xs text-foreground-400 mb-1 flex items-center gap-1">
            <i className="ri-time-line text-primary-500"></i> 更新/发布时间
          </p>
          <p className="text-xs font-semibold text-foreground-950 truncate">
            {currentDisplay?.published_at
              ? new Date(currentDisplay.published_at).toLocaleDateString()
              : currentDisplay?.submitted_at
                ? new Date(currentDisplay.submitted_at).toLocaleDateString()
                : '草稿未提交'}
          </p>
        </div>
      </div>

      {/* 联系方式卡片 */}
      <div className="p-5 rounded-xl bg-gray-50/80 border border-gray-200/60 space-y-3">
        <h3 className="text-xs font-semibold text-foreground-800 flex items-center gap-1.5">
          <i className="ri-contacts-book-2-line text-primary-500 text-sm"></i>
          联系方式信息 (授权展示)
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-white border border-gray-100">
            <div className="w-8 h-8 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
              <i className="ri-wechat-line"></i>
            </div>
            <div>
              <p className="text-[10px] text-foreground-400">微信号</p>
              <p className="text-xs font-medium text-foreground-900">
                {currentDisplay?.wechat_id || currentDisplay?.contact_wechat_id_masked || profile?.wechat_id || profile?.contact_wechat_id_masked || '未填'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-white border border-gray-100">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
              <i className="ri-phone-line"></i>
            </div>
            <div>
              <p className="text-[10px] text-foreground-400">联系电话</p>
              <p className="text-xs font-medium text-foreground-900">
                {currentDisplay?.phone || currentDisplay?.contact_phone_masked || profile?.phone || profile?.contact_phone_masked || '未填'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-white border border-gray-100">
            <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
              <i className="ri-mail-line"></i>
            </div>
            <div>
              <p className="text-[10px] text-foreground-400">电子邮箱</p>
              <p className="text-xs font-medium text-foreground-900">
                {currentDisplay?.email || currentDisplay?.contact_email_masked || profile?.email || profile?.contact_email_masked || '未填'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 技能标签 */}
      <div>
        <h3 className="text-xs font-semibold text-foreground-700 mb-2">技能标签</h3>
        <div className="flex flex-wrap gap-2">
          {currentDisplay?.skill_tags && currentDisplay.skill_tags.length > 0 ? (
            currentDisplay.skill_tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium"
              >
                {tag}
              </span>
            ))
          ) : (
            <p className="text-xs text-foreground-400">暂未添加技能标签</p>
          )}
        </div>
      </div>

      {/* 个人简介 */}
      <div>
        <h3 className="text-xs font-semibold text-foreground-700 mb-2">个人 / 团队简介</h3>
        <p className="text-xs text-foreground-600 leading-relaxed whitespace-pre-line p-4 rounded-xl bg-gray-50 border border-gray-100">
          {currentDisplay?.intro || '暂无个人简介'}
        </p>
      </div>

      {/* 服务范围说明 */}
      <div>
        <h3 className="text-xs font-semibold text-foreground-700 mb-2">服务范围说明</h3>
        <p className="text-xs text-foreground-600 leading-relaxed whitespace-pre-line p-4 rounded-xl bg-gray-50 border border-gray-100">
          {currentDisplay?.service_scope || '暂无服务范围说明'}
        </p>
      </div>

      {/* 代表作品集 */}
      <div>
        <h3 className="text-xs font-semibold text-foreground-700 mb-3 flex items-center gap-1.5">
          <i className="ri-play-circle-line text-primary-500"></i>
          代表作品集 <span className="text-foreground-400 font-normal">({works.length} 部)</span>
        </h3>
        {works.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {works.map((work, wIdx) => {
              const coverUrl = work.attachments?.access_url;
              const isVideo =
                work.attachments?.category === 'PORTFOLIO_VIDEO' ||
                (work.attachments?.category || '').toUpperCase().includes('VIDEO');
              return (
                <div
                  key={work.id || work.name || wIdx}
                  onClick={() => onPreviewWork(work)}
                  className="glass-card rounded-xl overflow-hidden group cursor-pointer border border-gray-200/60 hover:border-primary-300 hover:shadow-md transition-all bg-white"
                >
                  <div className="relative aspect-video overflow-hidden bg-gray-100 flex items-center justify-center">
                    {coverUrl ? (
                      isVideo ? (
                        <video
                          src={coverUrl}
                          muted
                          playsInline
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 pointer-events-none"
                        />
                      ) : (
                        <img
                          src={coverUrl}
                          alt={work.name}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      )
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 text-gray-400 group-hover:bg-primary-50/50 transition-colors">
                        <i
                          className={`${isVideo ? 'ri-vidicon-line' : 'ri-image-line'} text-2xl mb-1 text-gray-300 group-hover:text-primary-400 transition-colors`}
                        ></i>
                        <span className="text-[10px] font-medium">{isVideo ? '点击播放视频' : '点击查看作品'}</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/50 to-transparent"></div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-10 h-10 rounded-full bg-white/90 shadow-md flex items-center justify-center transform group-hover:scale-110 transition-transform">
                        <i
                          className={`${isVideo ? 'ri-play-fill text-xl text-primary-600 ml-0.5' : 'ri-zoom-in-line text-lg text-primary-600'}`}
                        ></i>
                      </div>
                    </div>
                  </div>
                  <div className="p-3">
                    <h4 className="font-semibold text-foreground-950 text-xs leading-snug mb-1 truncate group-hover:text-primary-600 transition-colors">
                      {work.name}
                    </h4>
                    {work.intro && <p className="text-[11px] text-foreground-500 line-clamp-2">{work.intro}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-6 text-center rounded-xl bg-gray-50 border border-gray-100 text-xs text-foreground-400">
            暂无公开展示的代表作品
          </div>
        )}
      </div>
    </div>
  );
}
